"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveAppBaseUrl = exports.registerAppVersion = exports.resolveAppVersion = void 0;
const logging_1 = require("./logging");
const namespaces_1 = require("./namespaces");
const registeredApps = {};
const idDetectionStrategies = [
    {
        name: 'Registered',
        detectVersion: (appId) => {
            return registeredApps[appId];
        },
    },
    {
        name: 'React',
        detectVersion: (appId) => {
            const versionMatch = appId.match(/^react[^\d]*(\d+(?:\.\d+)*)$/);
            return versionMatch ? versionMatch[1] : undefined;
        },
    },
];
const urlDetectionStrategies = [
    {
        name: 'Suffix',
        detectVersion: (part) => {
            const versionMatch = part.match(/~(\d+\.\d+\.\d+)/);
            return versionMatch ? versionMatch[1] : undefined;
        },
    },
    {
        name: 'Commit',
        detectVersion: (part) => {
            const versionMatch = part.match(/^[0-9a-f]{7,40}$/);
            return versionMatch ? versionMatch[0].substring(0, 7) : undefined;
        },
    },
    {
        name: 'LocalDev',
        detectVersion: (part) => {
            const versionMatch = part.match(/localhost/);
            if (versionMatch) {
                const port = part.split(':')[1];
                // signal we are running an import override
                // NB: we sent `localhost:${port}` before, but our AWS WAF blocked requests with `localhost` in it
                return `import_override_port_${port}`;
            }
            return undefined;
        },
    },
];
/**
 * Attempt to discover the version identifier for the given app identifier.
 *
 * Version may be 7-char commit hash or a typical number-based version.
 *
 * @param appId - app identifier used to resolve with SystemJS
 */
const resolveAppVersion = (appId) => {
    let version = undefined;
    try {
        for (const strategy of idDetectionStrategies) {
            version = strategy.detectVersion(appId);
            if (version) {
                (0, logging_1.verbose)(`[${strategy.name} Version] ${appId}: ${version}`);
                return version;
            }
        }
        // now try with paths
        const appUrl = System.resolve(appId);
        if (!appUrl) {
            (0, logging_1.warn)(`Could not resolve app '${appId}' to get app version.`);
            return undefined;
        }
        const appUrlParts = appUrl.split('/');
        for (const urlPart of appUrlParts) {
            if (!urlPart)
                continue; // may be if // in appUrl
            for (const strategy of urlDetectionStrategies) {
                version = strategy.detectVersion(urlPart);
                if (version) {
                    (0, logging_1.verbose)(`[${strategy.name} Version] ${appId}: ${version}`);
                    return version;
                }
            }
        }
        if (!version) {
            (0, logging_1.warn)(`Could not find version for app '${appId}'.`, appUrl);
        }
    }
    catch (ex) {
        (0, logging_1.error)(`Error getting version for app '${appId}'.`, ex);
    }
};
exports.resolveAppVersion = resolveAppVersion;
/**
 * Registers a version to resolve for a given app.
 *
 * This is useful for apps that do not embed their version in their URL.
 *
 * @param appId - SystemJS app module identifier
 * @param version - version to use for the app
 */
const registerAppVersion = (appId, version) => {
    registeredApps[appId] = version;
};
exports.registerAppVersion = registerAppVersion;
/**
 * Resolve the base URL for an app, including the current version path.
 *
 * @param appId - SystemJS app module identifier
 */
const resolveAppBaseUrl = (moduleName) => {
    const appUrl = System.resolve(moduleName);
    const pathParts = appUrl.split('/');
    pathParts.pop(); // remove file name part
    return pathParts.join('/');
};
exports.resolveAppBaseUrl = resolveAppBaseUrl;
(0, namespaces_1.addToBuiltNamespace)('Apps', 'resolveAppVersion', exports.resolveAppVersion);
(0, namespaces_1.addToBuiltNamespace)('Apps', 'registerAppVersion', exports.registerAppVersion);
(0, namespaces_1.addToBuiltNamespace)('Apps', 'resolveAppBaseUrl', exports.resolveAppBaseUrl);
